// src/components/SignIn.tsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  CssBaseline,
  FormControl,
  FormControlLabel,
  FormLabel,
  Link,
  TextField,
  Typography,
  Divider,
  Card as MuiCard,
  Stack,
  CircularProgress, // <<-- Import the spinner from MUI
  Alert, // Import Alert for rate limiting messages
  IconButton,
  InputAdornment,
} from "@mui/material";
import { styled } from "@mui/system";
import { signIn, rememberDevice, confirmSignIn } from "@aws-amplify/auth";
import { normalizeEmail } from "../../utils/email-utils";
// Removed notifySelfRegistered flow; notification now sent from PendingApproval
import StripyBackground from "./DiagonalCanvas";
import { useTenantConfig } from "../../contexts/TenantConfigContext";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

interface SignInProps {
  onSignInSuccess: () => void;
  onSignInFailure: (error: any, email?: string) => void;
  onNavigateToSignUp: () => void;
  onNavigateToResetPassword: () => void;
  onNavigateToConfirmSignUp?: () => void;
  externalError?: string;
}

// Simple client-side rate limiting to discourage brute force attacks
interface RateLimitState {
  failedAttempts: number;
  lastFailedAttempt: number;
  isBlocked: boolean;
  blockUntil: number;
}

// Get progressive delay based on failed attempts (in seconds)
const getProgressiveDelay = (failedAttempts: number): number => {
  if (failedAttempts < 3) return 0;
  if (failedAttempts < 5) return 5;   // 5 seconds after 3 failures
  if (failedAttempts < 8) return 15;  // 15 seconds after 5 failures
  if (failedAttempts < 12) return 30; // 30 seconds after 8 failures
  return 60; // 1 minute after 12+ failures
};

// Load rate limit state from localStorage
const loadRateLimitState = (): RateLimitState => {
  try {
    const stored = localStorage.getItem('login_rate_limit');
    if (stored) {
      const parsed = JSON.parse(stored);
      // Reset if more than 24 hours have passed
      if (Date.now() - parsed.lastFailedAttempt > 24 * 60 * 60 * 1000) {
        return { failedAttempts: 0, lastFailedAttempt: 0, isBlocked: false, blockUntil: 0 };
      }
      return parsed;
    }
  } catch (error) {
    console.error('Error loading rate limit state:', error);
  }
  return { failedAttempts: 0, lastFailedAttempt: 0, isBlocked: false, blockUntil: 0 };
};

// Save rate limit state to localStorage
const saveRateLimitState = (state: RateLimitState): void => {
  try {
    localStorage.setItem('login_rate_limit', JSON.stringify(state));
  } catch (error) {
    console.error('Error saving rate limit state:', error);
  }
};

// Styled "Card" for the form
const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(4),
  gap: theme.spacing(1.5), // Reduced from 2 for tighter spacing
  margin: "auto",
  maxHeight: "90vh", // Limit height on small screens
  overflowY: "auto", // Enable scrolling when content exceeds viewport
  // Hide scrollbar but keep functionality
  scrollbarWidth: "none", // Firefox
  "&::-webkit-scrollbar": {
    display: "none", // Chrome, Safari, Edge
  },
  msOverflowStyle: "none", // IE and Edge Legacy
  [theme.breakpoints.up("sm")]: {
    maxWidth: "450px",
    maxHeight: "none", // Remove height limit on larger screens
    overflowY: "visible", // Disable scrolling on larger screens
  },
  boxShadow:
    "hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px",
}));

// Container for the whole SignIn screen
const SignInContainer = styled(Stack)(({ theme }) => ({
  height: "100vh",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: theme.spacing(2),
  backgroundColor: "transparent", // Let the gradient show through
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
}));

const SignIn: React.FC<SignInProps> = ({
  onSignInSuccess,
  onSignInFailure,
  onNavigateToSignUp,
  onNavigateToResetPassword,
  onNavigateToConfirmSignUp,
  externalError,
}) => {
  console.log('SignIn component externalError:', externalError);
  const { config } = useTenantConfig();
  
  // Read email from URL params if present (e.g., from invitation email)
  const urlParams = new URLSearchParams(window.location.search);
  const emailFromUrl = urlParams.get('email') || '';
  
  const [email, setEmail] = useState(emailFromUrl);
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPasswordForm, setShowNewPasswordForm] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [passwordErrorMessage, setPasswordErrorMessage] = useState("");

  const [signInError, setSignInError] = useState("");
  
  console.log('SignIn component signInError:', signInError);

  // Track loading state while user is signing in
  const [loading, setLoading] = useState(false);

  // Rate limiting state
  const [rateLimitState, setRateLimitState] = useState<RateLimitState>(loadRateLimitState);
  const [countdown, setCountdown] = useState(0);

  // Update countdown timer
  useEffect(() => {
    const checkRateLimit = () => {
      const now = Date.now();
      if (rateLimitState.isBlocked && now < rateLimitState.blockUntil) {
        const remaining = Math.ceil((rateLimitState.blockUntil - now) / 1000);
        setCountdown(remaining);
      } else if (rateLimitState.isBlocked) {
        // Block period expired, reset state
        const newState = { ...rateLimitState, isBlocked: false, blockUntil: 0 };
        setRateLimitState(newState);
        saveRateLimitState(newState);
        setCountdown(0);
      }
    };

    checkRateLimit();
    const timer = setInterval(checkRateLimit, 1000);
    return () => clearInterval(timer);
  }, [rateLimitState]);

  // Reset rate limiting on successful login (called from parent)
  useEffect(() => {
    if (!externalError && !signInError) {
      // Successful login - reset rate limiting
      const resetState = { failedAttempts: 0, lastFailedAttempt: 0, isBlocked: false, blockUntil: 0 };
      setRateLimitState(resetState);
      saveRateLimitState(resetState);
    }
  }, [externalError, signInError]);

  const handleFailedAttempt = () => {
    const now = Date.now();
    const newFailedAttempts = rateLimitState.failedAttempts + 1;
    const delaySeconds = getProgressiveDelay(newFailedAttempts);
    
    const newState: RateLimitState = {
      failedAttempts: newFailedAttempts,
      lastFailedAttempt: now,
      isBlocked: delaySeconds > 0,
      blockUntil: delaySeconds > 0 ? now + (delaySeconds * 1000) : 0,
    };

    setRateLimitState(newState);
    saveRateLimitState(newState);

    if (delaySeconds > 0) {
      setCountdown(delaySeconds);
    }
  };

  const isRateLimited = rateLimitState.isBlocked && countdown > 0;

  // Password validation helper - matches Cognito policy exactly
  const validatePassword = (password: string): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    if (password.length < 6) {
      errors.push("At least 6 characters");
    }
    if (!/[a-z]/.test(password)) {
      errors.push("At least one lowercase letter");
    }
    if (!/[A-Z]/.test(password)) {
      errors.push("At least one uppercase letter");
    }
    if (!/[0-9]/.test(password)) {
      errors.push("At least one number");
    }
    if (!/[^a-zA-Z0-9]/.test(password)) {
      errors.push("At least one special character (e.g., !@#$%^&*)");
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  };

  // Check if new password form is valid (all requirements met)
  const isNewPasswordValid = showNewPasswordForm && 
    newPassword.length >= 6 &&
    /[a-z]/.test(newPassword) &&
    /[A-Z]/.test(newPassword) &&
    /[0-9]/.test(newPassword) &&
    /[^a-zA-Z0-9]/.test(newPassword) &&
    newPassword === confirmNewPassword &&
    confirmNewPassword.length > 0;

  const validateInputs = (): boolean => {
    let isValid = true;

    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setEmailError(true);
      setEmailErrorMessage("Please enter a valid email address.");
      isValid = false;
    } else {
      setEmailError(false);
      setEmailErrorMessage("");
    }

    if (!showNewPasswordForm) {
      if (!password || password.length < 6) {
        setPasswordError(true);
        setPasswordErrorMessage("Password must be at least 6 characters long.");
        isValid = false;
      } else {
        setPasswordError(false);
        setPasswordErrorMessage("");
      }
    } else {
      // Validate new password against Cognito requirements
      const passwordValidation = validatePassword(newPassword);
      
      if (!passwordValidation.isValid) {
        setPasswordError(true);
        setPasswordErrorMessage("Password must include:\n" + passwordValidation.errors.join("\n"));
        isValid = false;
      } else if (newPassword !== confirmNewPassword) {
        setPasswordError(true);
        setPasswordErrorMessage("Passwords do not match.");
        isValid = false;
      } else {
        setPasswordError(false);
        setPasswordErrorMessage("");
      }
    }

    return isValid;
  };

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log("=== handleSignIn called ===");
    setSignInError("");

    // Check rate limiting first
    if (isRateLimited) {
      setSignInError(`Too many failed attempts. Please wait ${countdown} seconds before trying again.`);
      return;
    }

    if (!validateInputs()) {
      console.log("Validation failed, returning early");
      return;
    }

    const normalizedEmail = normalizeEmail(email);

    console.log("Validation passed, proceeding with sign in flow");
    // Start showing the loading state
    setLoading(true);

    try {
      if (showNewPasswordForm) {
        console.log("About to call confirmSignIn for NEW_PASSWORD_REQUIRED challenge");
        const confirmResult = await confirmSignIn({ challengeResponse: newPassword });
        console.log("confirmSignIn result:", confirmResult);
        
        if (confirmResult.nextStep.signInStep !== 'DONE') {
          console.log("More steps required after confirmSignIn:", confirmResult.nextStep.signInStep);
          setSignInError(`Additional step required: ${confirmResult.nextStep.signInStep}`);
          setLoading(false);
          return;
        }
      } else {
        console.log("About to call signIn with:", { username: normalizedEmail, password: "***" });
        const { nextStep } = await signIn({ username: normalizedEmail, password });
        console.log("signIn nextStep:", nextStep);

        if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          console.log("NEW_PASSWORD_REQUIRED challenge received, showing new password form");
          setShowNewPasswordForm(true);
          setLoading(false);
          return;
        }

        if (nextStep.signInStep !== 'DONE') {
          console.log("Sign in not complete, next step:", nextStep.signInStep);
          // Handle other steps if necessary, for now we treat as failure if not DONE or NEW_PASSWORD
          if (nextStep.signInStep === 'CONFIRM_SIGN_UP') {
            setSignInError(
              "Your account is not confirmed. If you just signed up, check your email for a verification code. If you were moved from another product, try signing in with your existing email and password, or use Forgot password."
            );
            if (onNavigateToConfirmSignUp) {
                // We don't automatically navigate, but show the message and button
            }
          } else {
            setSignInError(`Unexpected sign-in step: ${nextStep.signInStep}`);
          }
          setLoading(false);
          return;
        }
      }

      console.log("Sign-in process successful, finishing up");
      
      if (rememberMe) {
        try {
          console.log("Remember me checked - registering device for 30 days");
          await rememberDevice();
          console.log("Device successfully registered for remember me");
        } catch (deviceError: any) {
          console.warn("Failed to register device for remember me:", deviceError);
        }
      }

      // Successful login - reset rate limiting
      const resetState = { failedAttempts: 0, lastFailedAttempt: 0, isBlocked: false, blockUntil: 0 };
      setRateLimitState(resetState);
      saveRateLimitState(resetState);

      onSignInSuccess();
    } catch (error: any) {
      console.error("Sign-in flow error:", error);
      
      // Handle failed attempt - apply rate limiting
      handleFailedAttempt();
      
      const message = error.message || "Failed to sign in.";
      setSignInError(message);
      
      // If we're in the new password form and it fails, we stay there
      if (!showNewPasswordForm) {
        setEmailError(true);
        setPasswordError(true);
        console.log("Calling onSignInFailure with error:", error, "email:", email);
        onSignInFailure(error, email);
      }
    } finally {
      // Stop loading once sign-in attempt finishes
      setLoading(false);
    }
  };

  const formatCountdown = (seconds: number): string => {
    if (seconds >= 60) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${seconds}s`;
  };

  return (
    <>
      {/* Diagonal gradient background */}
      <StripyBackground />
      <CssBaseline />

      {/* Company name/logo in top-left */}
      <Box
        sx={{
          position: "absolute",
          top: "1rem",
          left: "1rem",
          zIndex: 2,
          color: "#fff",
          fontWeight: "bold",
          fontSize: "1.5rem",
          display: "flex",
          alignItems: "center",
          gap: "20px",
        }}
      >
        <img src={config?.branding.logoUrl || "/brand/app-icon.svg"} alt={`${config?.branding.displayName || 'RAF'} Logo`} height="32" style={{ width: 'auto', borderRadius: '4px' }} />
        {config?.branding.displayName || 'RAF'}
      </Box>
      <SignInContainer>
        <Card>
          <Typography
            component="h1"
            variant="h4"
            sx={{ width: "100%", textAlign: "center" }}
          >
            {showNewPasswordForm ? "Set New Password" : "Sign In"}
          </Typography>

          <Divider sx={{ mt: '5px', mb: 2 }} />

          {showNewPasswordForm && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This is your first login. Please set a new password to continue.
            </Alert>
          )}

          {/* Rate limiting alert */}
          {isRateLimited && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Too many failed login attempts.</strong><br />
                Please wait {formatCountdown(countdown)} before trying again.
                {rateLimitState.failedAttempts >= 5 && (
                  <><br /><em>Consider using "Forgot Password" if you're having trouble.</em></>
                )}
              </Typography>
            </Alert>
          )}

          {/* Display error or success message if available */}
          {(externalError || signInError) && !isRateLimited && (
            <Box sx={{ textAlign: "center", mb: 2 }}>
              <Typography
                variant="body2"
                color={
                  (externalError || signInError)?.includes('successfully') || 
                  (externalError || signInError)?.includes('confirmed successfully')
                    ? "success.main" 
                    : "error"
                }
                sx={{ mb: 1 }}
              >
                {externalError || signInError}
              </Typography>
              {(externalError?.includes('not confirmed') || signInError?.includes('not confirmed')) && onNavigateToConfirmSignUp && (
                <Button
                  variant="text"
                  size="small"
                  onClick={onNavigateToConfirmSignUp}
                  sx={{ textTransform: 'none' }}
                >
                  Go to Confirmation Page
                </Button>
              )}
            </Box>
          )}
          


          <Box
            component="form"
            onSubmit={handleSignIn}
            autoComplete="on"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {!showNewPasswordForm ? (
              <>
                <FormControl>
                  <FormLabel>Email</FormLabel>
                  <TextField
                    error={emailError}
                    helperText={emailErrorMessage}
                    id="email"
                    type="email"
                    name="username"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    fullWidth
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Password</FormLabel>
                  <TextField
                    error={passwordError}
                    helperText={passwordErrorMessage}
                    id="password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••"
                    required
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                  }
                  label="Remember me for 30 days"
                  sx={{ alignSelf: "flex-start" }}
                />
              </>
            ) : (
              <>
                <FormControl>
                  <FormLabel>New Password</FormLabel>
                  <TextField
                    error={passwordError}
                    helperText={passwordError ? passwordErrorMessage : ""}
                    id="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    name="newPassword"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    fullWidth
                    FormHelperTextProps={{
                      sx: { whiteSpace: 'pre-line' }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            edge="end"
                          >
                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Confirm New Password</FormLabel>
                  <TextField
                    error={passwordError && confirmNewPassword.length > 0 && newPassword !== confirmNewPassword}
                    helperText={passwordError && confirmNewPassword.length > 0 && newPassword !== confirmNewPassword ? "Passwords do not match" : ""}
                    id="confirmNewPassword"
                    type={showConfirmNewPassword ? "text" : "password"}
                    name="confirmNewPassword"
                    autoComplete="new-password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                            edge="end"
                          >
                            {showConfirmNewPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>

                {/* Password Requirements Checklist - always visible to prevent UI jump */}
                <Box sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.75, fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>
                    Password must include:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: newPassword.length >= 6 ? '#00e5c3' : '#ff6b6b',
                        fontSize: '0.75rem'
                      }}
                    >
                      {newPassword.length >= 6 ? '✓' : '✗'} At least 6 characters long
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: /[a-z]/.test(newPassword) ? '#00e5c3' : '#ff6b6b',
                        fontSize: '0.75rem'
                      }}
                    >
                      {/[a-z]/.test(newPassword) ? '✓' : '✗'} At least one lowercase letter (a-z)
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: /[A-Z]/.test(newPassword) ? '#00e5c3' : '#ff6b6b',
                        fontSize: '0.75rem'
                      }}
                    >
                      {/[A-Z]/.test(newPassword) ? '✓' : '✗'} At least one uppercase letter (A-Z)
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: /[0-9]/.test(newPassword) ? '#00e5c3' : '#ff6b6b',
                        fontSize: '0.75rem'
                      }}
                    >
                      {/[0-9]/.test(newPassword) ? '✓' : '✗'} At least one number (0-9)
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: /[^a-zA-Z0-9]/.test(newPassword) ? '#00e5c3' : '#ff6b6b',
                        fontSize: '0.75rem'
                      }}
                    >
                      {/[^a-zA-Z0-9]/.test(newPassword) ? '✓' : '✗'} At least one special character (!@#$%^&*)
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: confirmNewPassword && newPassword === confirmNewPassword ? '#00e5c3' : '#ff6b6b',
                        fontSize: '0.75rem'
                      }}
                    >
                      {confirmNewPassword && newPassword === confirmNewPassword ? '✓' : '✗'} Passwords match
                    </Typography>
                  </Box>
                </Box>
              </>
            )}

            {/* The "Sign In" button now shows loading spinner & text if loading */}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading || isRateLimited || (showNewPasswordForm && !isNewPasswordValid)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "hsl(var(--accent))",
                "&:hover": {
                  backgroundColor: "hsl(var(--accent) / 0.9)",
                },
                "&:disabled": {
                  backgroundColor: isRateLimited ? "hsl(var(--accent) / 0.6)" : "#cccccc",
                  color: isRateLimited ? "white" : "rgba(0, 0, 0, 0.26)",
                },
              }}
            >
              {loading ? (
                <>
                  <CircularProgress
                    color="inherit"
                    size={20}
                    sx={{ marginRight: 1 }}
                  />
                  {showNewPasswordForm ? "Setting password..." : "Signing in..."}
                </>
              ) : isRateLimited ? (
                `Wait ${formatCountdown(countdown)}`
              ) : (
                showNewPasswordForm ? "Set Password & Sign In" : "Sign In"
              )}
            </Button>
            {showNewPasswordForm && (
              <Button
                variant="text"
                fullWidth
                onClick={() => {
                  setShowNewPasswordForm(false);
                  setSignInError("");
                  setPasswordError(false);
                  setPasswordErrorMessage("");
                }}
                sx={{ textTransform: "none" }}
              >
                Back to Sign In
              </Button>
            )}
          </Box>

          <Divider sx={{ my: 1 }}>or</Divider>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="body2" sx={{ textAlign: "left" }}>
              Don&apos;t have an account?{" "}
              <Link
                onClick={onNavigateToSignUp}
                component="button"
                variant="body2"
                sx={{ 
                  textTransform: "none",
                  color: "hsl(var(--accent))",
                  verticalAlign: "baseline",
                  display: "inline",
                  padding: 0,
                  minHeight: "auto",
                  textDecoration: "none",
                  "&:hover": {
                    color: "hsl(var(--accent) / 0.85)",
                    textDecoration: "none",
                  },
                }}
              >
                Sign Up
              </Link>
            </Typography>
            <Typography variant="body2" sx={{ textAlign: "left" }}>
              Forgot your password?{" "}
              <Link
                onClick={onNavigateToResetPassword}
                component="button"
                variant="body2"
                sx={{
                  textTransform: "none",
                  color: "hsl(var(--accent))",
                  verticalAlign: "baseline",
                  display: "inline",
                  padding: 0,
                  minHeight: "auto",
                  textDecoration: "none",
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: "hsl(var(--accent) / 0.85)",
                    textDecoration: "none",
                  },
                }}
              >
                Reset Password
              </Link>
            </Typography>
          </Box>
        </Card>
      </SignInContainer>
    </>
  );
};

export default SignIn;
